import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useUser } from '../../contexts/UserContext';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Icon,
  Flex,
  useColorModeValue,
  Progress,
  Avatar,
  Tooltip,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Grid,
  IconButton,
  Tag,
  TagLabel,
  TagLeftIcon,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Card,
  CardBody,
  CardHeader,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Divider,
} from '@chakra-ui/react';
import { useCustomToast } from '../../utils/customToast';
import { 
  FaSearch, 
  FaChartLine, 
  FaEye,
  FaArrowUp,
  FaArrowDown,
  FaExternalLinkAlt,
  FaStar,
  FaTrophy,
  FaMedal,
  FaAward,
  FaChartBar,
  FaChartPie,
  FaLightbulb,
  FaRocket,
  FaCrown,
  FaKey,
  FaDollarSign,
  FaGlobe,
  FaFilter,
  FaBullseye,
  FaFireAlt,
  FaUsers,
} from 'react-icons/fa';
import { SectionLabel } from './DashboardWrapper';
import { 
  BarChart, 
  Bar, 
  PieChart,
  Pie,
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  ReferenceLine,
  Legend,
  AreaChart,
  Area,
} from 'recharts';
import BusinessReviewModal from '../BusinessReviewModal';
import { getBusinessImages } from '../../services/imageService';
import { getYearData } from '../../services/yearService';
import { 
  getKeywordSuggestions, 
  getDomainOrganicKeywords,
  getDomainPaidKeywords,
  getDomainOverview,
} from '../../services/keywordIntelligenceService';
import { logger } from '../../utils/logger';
import { createFavoriteStarPieLabelRenderer } from '../FavoriteStarIndicator';
import {
  hasCompetitiveIntel,
  getTopKeywordsByVolume,
  getKeywordOpportunities,
  getTotalOrganicKeywords,
  formatNumber as formatNum,
  formatCurrency,
  getKeywordDifficultyColor,
  getDomainStrength,
  getMonthlyOrganicClicks,
  getMonthlyOrganicValue,
  getSEOKeywords,
  getSEOCompetitors,
  getMonthlyPPCBudget,
  getTrafficValue,
} from '../../utils/competitiveIntelHelpers';

// Modern color palette
const COLORS = {
  primary: '#3B82F6',
  secondary: '#8B5CF6',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  info: '#06B6D4',
  chart: [
    '#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444',
    '#06B6D4', '#EC4899', '#14B8A6', '#6366F1', '#F97316',
  ],
};

const SpyfuKeywordDashboard = ({ reviewsData = [], demoMode, demoData }) => {
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [isBusinessModalOpen, setIsBusinessModalOpen] = useState(false);
  const [businessImages, setBusinessImages] = useState({});
  const [searchKeyword, setSearchKeyword] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [keywordSearchResults, setKeywordSearchResults] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [domainKeywords, setDomainKeywords] = useState({ organic: null, paid: null });
  const [isDomainLoading, setIsDomainLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const toast = useCustomToast();
  
  // Get favorite URIs from user context
  const { favoriteUris } = useUser();

  // Create a map from shortened business names to their maps_uri for favorites check
  const businessNameToUriMap = useMemo(() => {
    const map = {};
    if (reviewsData) {
      reviewsData.forEach(business => {
        const shortName = business.name?.split(' ').slice(0, 2).join(' ');
        if (shortName && business.maps_uri) {
          map[shortName] = business.maps_uri;
        }
      });
    }
    return map;
  }, [reviewsData]);

  // Custom label renderer for pie charts
  const FavoriteStarPieLabel = useMemo(() => {
    return createFavoriteStarPieLabelRenderer({
      businessNameToUriMap,
      favoriteUris
    });
  }, [businessNameToUriMap, favoriteUris]);

  // Year data state
  const [yearData, setYearData] = useState([]);
  const [combinedData, setCombinedData] = useState([]);
  
  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const subtitleColor = useColorModeValue('gray.600', 'gray.400');
  const mutedColor = useColorModeValue('gray.500', 'gray.500');
  const statBg = useColorModeValue('gray.50', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const glassBg = useColorModeValue('rgba(255,255,255,0.9)', 'rgba(26,32,44,0.9)');

  // Fetch business images
  useEffect(() => {
    if (demoMode) return;
    let mounted = true;
    const fetchImages = async () => {
      try {
        const images = await getBusinessImages();
        if (mounted && images) {
          const imageMap = {};
          images.forEach(img => {
            if (img.maps_uri && img.high_res_image_url_fetched) {
              imageMap[img.maps_uri] = img.high_res_image_url_fetched;
            }
          });
          setBusinessImages(imageMap);
        }
      } catch (error) {
        logger.error('Error fetching business images:', error);
      }
    };
    fetchImages();
    return () => { mounted = false; };
  }, []);

  // Fetch year data
  useEffect(() => {
    if (demoMode && demoData) {
      setYearData(demoData.yearData || []);
      setCombinedData(demoData.combinedData || []);
      return;
    }
    const fetchYearData = async () => {
      try {
        const yearDataResponse = await getYearData();
        setYearData(yearDataResponse);

        const combined = reviewsData.map((currentData) => {
          const historicalData = yearDataResponse.find(
            (yd) => yd.maps_uri === currentData.maps_uri
          );
          return {
            ...currentData,
            historicalData: historicalData?.data || null,
          };
        });
        setCombinedData(combined);
      } catch (error) {
        logger.error('Error fetching year data:', error);
      }
    };

    if (reviewsData.length > 0) {
      fetchYearData();
    }
  }, [reviewsData]);

  // Handle business click
  const handleBusinessClick = useCallback((business) => {
    const businessWithData = combinedData.find(
      (b) => b.maps_uri === business.maps_uri
    );
    setSelectedBusiness(businessWithData || business);
    setIsBusinessModalOpen(true);
  }, [combinedData]);

  const closeBusinessModal = () => {
    setIsBusinessModalOpen(false);
    setSelectedBusiness(null);
  };

  // Format number helper
  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
  };

  // Businesses with keyword data
  const businessesWithKeywords = useMemo(() => {
    return reviewsData.filter(business => hasCompetitiveIntel(business));
  }, [reviewsData]);

  // Keyword statistics
  const keywordStats = useMemo(() => {
    if (!businessesWithKeywords.length) return null;

    const totalKeywords = businessesWithKeywords.reduce((sum, business) => {
      return sum + getTotalOrganicKeywords(business);
    }, 0);

    const avgKeywords = Math.round(totalKeywords / businessesWithKeywords.length);

    // Get all top keywords across all businesses
    const allTopKeywords = [];
    businessesWithKeywords.forEach(business => {
      const topKws = getTopKeywordsByVolume(business, 10);
      topKws.forEach(kw => {
        allTopKeywords.push({
          ...kw,
          businessName: business.name,
          businessDomain: business.spyfu_data?.domain
        });
      });
    });

    // Sort by search volume and get top results
    const topKeywords = allTopKeywords
      .sort((a, b) => (b.searchVolume || 0) - (a.searchVolume || 0))
      .slice(0, 20);

    // Get all keyword opportunities
    const allOpportunities = [];
    businessesWithKeywords.forEach(business => {
      const opps = getKeywordOpportunities(business, {
        minVolume: 100,
        maxDifficulty: 50,
        minRank: 11
      });
      opps.forEach(opp => {
        allOpportunities.push({
          ...opp,
          businessName: business.name,
          businessDomain: business.spyfu_data?.domain
        });
      });
    });

    // Calculate total organic value and clicks
    const totalOrganicValue = businessesWithKeywords.reduce((sum, business) => {
      return sum + getMonthlyOrganicValue(business);
    }, 0);

    const totalOrganicClicks = businessesWithKeywords.reduce((sum, business) => {
      return sum + getMonthlyOrganicClicks(business);
    }, 0);

    const avgDomainStrength = Math.round(
      businessesWithKeywords.reduce((sum, business) => sum + getDomainStrength(business), 0) / 
      businessesWithKeywords.length
    );

    return {
      totalBusinesses: businessesWithKeywords.length,
      totalKeywords,
      avgKeywords,
      topKeywords,
      opportunities: allOpportunities.slice(0, 20),
      totalOrganicValue,
      totalOrganicClicks,
      avgDomainStrength
    };
  }, [businessesWithKeywords]);

  // Top businesses by keyword count
  const topBusinessesByKeywords = useMemo(() => {
    return [...businessesWithKeywords]
      .map(business => ({
        ...business,
        keywordCount: getTotalOrganicKeywords(business),
        organicValue: getMonthlyOrganicValue(business),
        organicClicks: getMonthlyOrganicClicks(business),
        domainStrength: getDomainStrength(business)
      }))
      .sort((a, b) => b.keywordCount - a.keywordCount)
      .slice(0, 10);
  }, [businessesWithKeywords]);

  // Keyword distribution by difficulty
  const keywordDifficultyDistribution = useMemo(() => {
    const distribution = { easy: 0, medium: 0, hard: 0, veryHard: 0 };
    
    businessesWithKeywords.forEach(business => {
      const keywords = getSEOKeywords(business, 1000);
      keywords.forEach(kw => {
        const diff = kw.keywordDifficulty || 0;
        if (diff < 30) distribution.easy++;
        else if (diff < 50) distribution.medium++;
        else if (diff < 70) distribution.hard++;
        else distribution.veryHard++;
      });
    });

    return [
      { name: 'Easy (0-29)', value: distribution.easy, color: COLORS.success },
      { name: 'Medium (30-49)', value: distribution.medium, color: COLORS.warning },
      { name: 'Hard (50-69)', value: distribution.hard, color: '#F97316' },
      { name: 'Very Hard (70+)', value: distribution.veryHard, color: COLORS.danger },
    ].filter(d => d.value > 0);
  }, [businessesWithKeywords]);

  // Search keyword handler
  const handleKeywordSearch = async () => {
    if (!searchKeyword.trim()) return;
    
    setIsSearching(true);
    try {
      const results = await getKeywordSuggestions(searchKeyword.trim());
      setKeywordSearchResults(results);
      toast({
        title: 'Keyword Research Complete',
        description: `Found ${results?.data?.results?.length || 0} related keywords`,
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      logger.error('Error searching keywords:', error);
      toast({
        title: 'Search Failed',
        description: error.message || 'Failed to search keywords',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSearching(false);
    }
  };

  // Load domain keywords
  const loadDomainKeywords = async (domain) => {
    if (!domain) return;
    
    setIsDomainLoading(true);
    setSelectedDomain(domain);
    
    try {
      const [organicRes, paidRes] = await Promise.all([
        getDomainOrganicKeywords(domain),
        getDomainPaidKeywords(domain)
      ]);
      
      setDomainKeywords({
        organic: organicRes?.data?.results || [],
        paid: paidRes?.data?.results || []
      });
    } catch (error) {
      logger.error('Error loading domain keywords:', error);
      toast({
        title: 'Failed to Load Keywords',
        description: error.message || 'Could not fetch domain keywords',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsDomainLoading(false);
    }
  };

  // Sorted arrays for BusinessReviewModal
  const sortedByTotal = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData].sort(
      (a, b) => (b.rating_count || 0) - (a.rating_count || 0)
    );
  }, [combinedData]);

  const sortedBySentiment = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData].map((business) => {
      const sentimentBreakdown = business.historicalData?.sentimentBreakdown || {
        positive: 0,
        neutral: 0,
        negative: 0,
      };
      const total =
        sentimentBreakdown.positive +
        sentimentBreakdown.neutral +
        sentimentBreakdown.negative;
      const sentimentScore =
        total > 0 ? (sentimentBreakdown.positive / total) * 100 : 0;
      return { ...business, sentimentScore };
    }).sort((a, b) => b.sentimentScore - a.sentimentScore);
  }, [combinedData]);

  const sortedByGrowth = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData].map((business) => {
      const monthlyData = business.historicalData?.monthlyReviewCounts || {};
      const months = Object.keys(monthlyData).sort();
      const recentMonths = months.slice(-3);
      const totalGrowth = recentMonths.reduce(
        (sum, month) => sum + (monthlyData[month] || 0),
        0
      );
      return { ...business, totalGrowth };
    }).sort((a, b) => b.totalGrowth - a.totalGrowth);
  }, [combinedData]);

  const sortedByTraffic = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData]
      .map((business) => {
        const trafficValue = getTrafficValue(business) || 0;
        return { ...business, trafficValue };
      })
      .filter((business) => business.trafficValue > 0)
      .sort((a, b) => b.trafficValue - a.trafficValue);
  }, [combinedData]);

  // Empty state
  if (!reviewsData || reviewsData.length === 0) {
    return (
      <Box>
        {!demoMode && <SectionLabel description="Discover high-value keywords, analyze competitor keyword strategies, and identify opportunities to improve your search rankings.">
          Keyword Intelligence
        </SectionLabel>}

        <Flex direction="column" align="center" justify="center" minH="400px" p={8}>
          <Icon as={FaKey} boxSize={16} color="gray.300" mb={4} />
          <Heading size="md" color="gray.500" mb={2}>No Keyword Data Available</Heading>
          <Text color="gray.400" textAlign="center">
            Keyword analytics will appear here once data is available for your market.
          </Text>
        </Flex>
      </Box>
    );
  }

  // No businesses with keyword data
  if (!businessesWithKeywords.length) {
    return (
      <Box>
        {!demoMode && <SectionLabel description="Discover high-value keywords, analyze competitor keyword strategies, and identify opportunities to improve your search rankings.">
          Keyword Intelligence
        </SectionLabel>}

        <Alert status="info" borderRadius="lg" mb={6}>
          <AlertIcon />
          <Box>
            <AlertTitle>No Keyword Data Yet</AlertTitle>
            <AlertDescription>
              SpyFu keyword data is being collected for businesses in your market. Check back soon!
            </AlertDescription>
          </Box>
        </Alert>

        {/* Still show keyword research tool */}
        <Box bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} p={6}>
          <Heading size="md" mb={4}>
            <Icon as={FaSearch} mr={2} color="purple.500" />
            Keyword Research
          </Heading>
          <Text color={subtitleColor} mb={4}>
            Search for keyword ideas and analyze search volume, difficulty, and competition.
          </Text>
          <HStack mb={4}>
            <InputGroup size="lg">
              <InputLeftElement>
                <Icon as={FaSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Enter a keyword to research..."
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleKeywordSearch()}
              />
            </InputGroup>
            <Button
              colorScheme="purple"
              size="lg"
              onClick={handleKeywordSearch}
              isLoading={isSearching}
              loadingText="Searching..."
            >
              Research
            </Button>
          </HStack>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Top 3 Keyword Leaders Podium */}
      {topBusinessesByKeywords.length >= 3 && (
        <Box
          display={{ base: 'none', md: 'block' }}
          bg="transparent"
          mb={8}
        >
          <HStack spacing={2} mb={6} align="center">
            <Box
              bgGradient="linear(to-r, purple.500, blue.500)"
              p={2}
              borderRadius="lg"
            >
              <Icon as={FaKey} color="white" boxSize={5} />
            </Box>
            <Heading 
              size="lg" 
              bgGradient="linear(to-r, purple.600, blue.600)"
              bgClip="text"
              fontWeight="bold"
            >
              Keyword Leaders
            </Heading>
          </HStack>

          <Flex justify="center" align="flex-end" gap={6} w="full">
            {/* Silver - Second Place */}
            <Box
              bg={cardBg}
              borderRadius="2xl"
              textAlign="center"
              position="relative"
              cursor="pointer"
              onClick={() => topBusinessesByKeywords[1] && handleBusinessClick(topBusinessesByKeywords[1])}
              transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              _hover={{ transform: 'translateY(-8px)', shadow: '2xl' }}
              overflow="hidden"
              flex="1"
              maxW="320px"
              boxShadow="xl"
              borderWidth="1px"
              borderColor={borderColor}
            >
              <Box h="4px" bgGradient="linear(to-r, gray.300, gray.500, gray.300)" />
              <VStack p={5} spacing={3}>
                <Flex
                  position="absolute"
                  top={3}
                  left={3}
                  w="28px"
                  h="28px"
                  borderRadius="full"
                  bgGradient="linear(to-br, gray.300, gray.500)"
                  justify="center"
                  align="center"
                  shadow="md"
                >
                  <Text color="white" fontWeight="bold" fontSize="sm">2</Text>
                </Flex>
                
                <Icon as={FaMedal} color="gray.400" boxSize={5} />
                
                <Avatar 
                  name={topBusinessesByKeywords[1]?.name} 
                  src={businessImages[topBusinessesByKeywords[1]?.maps_uri] || ''} 
                  size="md"
                />
                
                <VStack spacing={0}>
                  <Tooltip label={topBusinessesByKeywords[1]?.name} placement="top" hasArrow>
                    <Heading size="sm" noOfLines={1}>{topBusinessesByKeywords[1]?.name}</Heading>
                  </Tooltip>
                  <Text fontWeight="extrabold" fontSize="2xl" color="purple.600">
                    {formatNumber(topBusinessesByKeywords[1]?.keywordCount || 0)}
                  </Text>
                  <Text fontSize="xs" color="gray.500" textTransform="uppercase">keywords</Text>
                </VStack>
                
                <HStack spacing={3} fontSize="xs" color={subtitleColor}>
                  <VStack spacing={0}>
                    <Text fontWeight="bold">{formatCurrency(topBusinessesByKeywords[1]?.organicValue || 0)}</Text>
                    <Text>value/mo</Text>
                  </VStack>
                  <VStack spacing={0}>
                    <Text fontWeight="bold">{topBusinessesByKeywords[1]?.domainStrength || 0}</Text>
                    <Text>strength</Text>
                  </VStack>
                </HStack>
              </VStack>
            </Box>

            {/* Gold - First Place */}
            <Box
              bg={cardBg}
              borderRadius="2xl"
              textAlign="center"
              position="relative"
              cursor="pointer"
              onClick={() => topBusinessesByKeywords[0] && handleBusinessClick(topBusinessesByKeywords[0])}
              transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              _hover={{ transform: 'translateY(-12px) scale(1.02)', shadow: '0 25px 50px -12px rgba(147, 51, 234, 0.3)' }}
              overflow="hidden"
              flex="1.15"
              maxW="380px"
              boxShadow="0 20px 40px -12px rgba(147, 51, 234, 0.25)"
              zIndex={2}
              mb={4}
              borderWidth="1px"
              borderColor="purple.200"
            >
              <Box
                h="6px"
                bgGradient="linear(to-r, purple.400, blue.400, purple.400)"
                backgroundSize="200% 100%"
                animation="shimmer 2s linear infinite"
                sx={{
                  '@keyframes shimmer': {
                    '0%': { backgroundPosition: '200% 0' },
                    '100%': { backgroundPosition: '-200% 0' },
                  },
                }}
              />
              <VStack p={6} spacing={4}>
                <Flex
                  position="absolute"
                  top={3}
                  left={3}
                  w="32px"
                  h="32px"
                  borderRadius="full"
                  bgGradient="linear(to-br, purple.400, blue.500)"
                  justify="center"
                  align="center"
                  shadow="lg"
                >
                  <Text color="white" fontWeight="bold" fontSize="md">1</Text>
                </Flex>
                
                <Icon as={FaTrophy} color="purple.400" boxSize={8} filter="drop-shadow(0 2px 4px rgba(147, 51, 234, 0.5))" />
                
                <Avatar 
                  name={topBusinessesByKeywords[0]?.name} 
                  src={businessImages[topBusinessesByKeywords[0]?.maps_uri] || ''} 
                  size="lg"
                  borderWidth="3px"
                  borderColor="purple.200"
                />
                
                <VStack spacing={0}>
                  <Tooltip label={topBusinessesByKeywords[0]?.name} placement="top" hasArrow>
                    <Heading size="md" noOfLines={1}>{topBusinessesByKeywords[0]?.name}</Heading>
                  </Tooltip>
                  <Text 
                    fontWeight="extrabold" 
                    fontSize="3xl" 
                    bgGradient="linear(to-r, purple.500, blue.500)"
                    bgClip="text"
                  >
                    {formatNumber(topBusinessesByKeywords[0]?.keywordCount || 0)}
                  </Text>
                  <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                    organic keywords
                  </Text>
                </VStack>
                
                <HStack spacing={4} fontSize="sm" color={subtitleColor}>
                  <VStack spacing={0}>
                    <Text fontWeight="bold" color="green.500">{formatCurrency(topBusinessesByKeywords[0]?.organicValue || 0)}</Text>
                    <Text fontSize="xs">value/mo</Text>
                  </VStack>
                  <VStack spacing={0}>
                    <Text fontWeight="bold" color="blue.500">{formatNumber(topBusinessesByKeywords[0]?.organicClicks || 0)}</Text>
                    <Text fontSize="xs">clicks/mo</Text>
                  </VStack>
                  <VStack spacing={0}>
                    <Text fontWeight="bold" color="purple.500">{topBusinessesByKeywords[0]?.domainStrength || 0}</Text>
                    <Text fontSize="xs">strength</Text>
                  </VStack>
                </HStack>
              </VStack>
            </Box>

            {/* Bronze - Third Place */}
            <Box
              bg={cardBg}
              borderRadius="2xl"
              textAlign="center"
              position="relative"
              cursor="pointer"
              onClick={() => topBusinessesByKeywords[2] && handleBusinessClick(topBusinessesByKeywords[2])}
              transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              _hover={{ transform: 'translateY(-8px)', shadow: '2xl' }}
              overflow="hidden"
              flex="1"
              maxW="320px"
              boxShadow="xl"
              borderWidth="1px"
              borderColor={borderColor}
            >
              <Box h="4px" bgGradient="linear(to-r, orange.200, orange.400, orange.200)" />
              <VStack p={5} spacing={3}>
                <Flex
                  position="absolute"
                  top={3}
                  left={3}
                  w="28px"
                  h="28px"
                  borderRadius="full"
                  bgGradient="linear(to-br, orange.300, orange.500)"
                  justify="center"
                  align="center"
                  shadow="md"
                >
                  <Text color="white" fontWeight="bold" fontSize="sm">3</Text>
                </Flex>
                
                <Icon as={FaAward} color="orange.400" boxSize={5} />
                
                <Avatar 
                  name={topBusinessesByKeywords[2]?.name} 
                  src={businessImages[topBusinessesByKeywords[2]?.maps_uri] || ''} 
                  size="md"
                />
                
                <VStack spacing={0}>
                  <Tooltip label={topBusinessesByKeywords[2]?.name} placement="top" hasArrow>
                    <Heading size="sm" noOfLines={1}>{topBusinessesByKeywords[2]?.name}</Heading>
                  </Tooltip>
                  <Text fontWeight="extrabold" fontSize="2xl" color="orange.500">
                    {formatNumber(topBusinessesByKeywords[2]?.keywordCount || 0)}
                  </Text>
                  <Text fontSize="xs" color="gray.500" textTransform="uppercase">keywords</Text>
                </VStack>
                
                <HStack spacing={3} fontSize="xs" color={subtitleColor}>
                  <VStack spacing={0}>
                    <Text fontWeight="bold">{formatCurrency(topBusinessesByKeywords[2]?.organicValue || 0)}</Text>
                    <Text>value/mo</Text>
                  </VStack>
                  <VStack spacing={0}>
                    <Text fontWeight="bold">{topBusinessesByKeywords[2]?.domainStrength || 0}</Text>
                    <Text>strength</Text>
                  </VStack>
                </HStack>
              </VStack>
            </Box>
          </Flex>
        </Box>
      )}

      {/* Section Label */}
      {!demoMode && <SectionLabel description="Discover high-value keywords, analyze competitor keyword strategies, and identify opportunities to improve your search rankings.">
        Keyword Intelligence
      </SectionLabel>}

      {/* Market Overview Stats */}
      {keywordStats && (
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={8}>
          <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel color={subtitleColor}>Total Keywords</StatLabel>
                <StatNumber color="purple.500">{formatNum(keywordStats.totalKeywords)}</StatNumber>
                <StatHelpText>Across {keywordStats.totalBusinesses} businesses</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel color={subtitleColor}>Monthly Organic Value</StatLabel>
                <StatNumber color="green.500">{formatCurrency(keywordStats.totalOrganicValue)}</StatNumber>
                <StatHelpText>Est. market value</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel color={subtitleColor}>Monthly Clicks</StatLabel>
                <StatNumber color="blue.500">{formatNum(keywordStats.totalOrganicClicks)}</StatNumber>
                <StatHelpText>Organic search traffic</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel color={subtitleColor}>Avg Domain Strength</StatLabel>
                <StatNumber color="orange.500">{keywordStats.avgDomainStrength}</StatNumber>
                <StatHelpText>Market average (0-100)</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>
      )}

      {/* Main Tabs */}
      <Tabs variant="soft-rounded" colorScheme="purple" mb={8} index={activeTab} onChange={setActiveTab}>
        <TabList mb={6} flexWrap="wrap" gap={2}>
          <Tab _selected={{ bg: 'purple.500', color: 'white' }}>
            <HStack spacing={2}>
              <Icon as={FaChartBar} />
              <Text>Market Overview</Text>
            </HStack>
          </Tab>
          <Tab _selected={{ bg: 'purple.500', color: 'white' }}>
            <HStack spacing={2}>
              <Icon as={FaKey} />
              <Text>Top Keywords</Text>
            </HStack>
          </Tab>
          <Tab _selected={{ bg: 'purple.500', color: 'white' }}>
            <HStack spacing={2}>
              <Icon as={FaLightbulb} />
              <Text>Opportunities</Text>
            </HStack>
          </Tab>
          <Tab _selected={{ bg: 'purple.500', color: 'white' }}>
            <HStack spacing={2}>
              <Icon as={FaSearch} />
              <Text>Keyword Research</Text>
            </HStack>
          </Tab>
          <Tab _selected={{ bg: 'purple.500', color: 'white' }}>
            <HStack spacing={2}>
              <Icon as={FaGlobe} />
              <Text>Domain Analysis</Text>
            </HStack>
          </Tab>
        </TabList>

        <TabPanels>
          {/* Market Overview Panel */}
          <TabPanel p={0}>
            <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6}>
              {/* Keywords by Business */}
              <Box bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} p={6}>
                <HStack justify="space-between" mb={4}>
                  <VStack align="start" spacing={0}>
                    <Heading size="md">Keywords by Business</Heading>
                    <Text fontSize="sm" color={subtitleColor}>Top 10 businesses by keyword count</Text>
                  </VStack>
                </HStack>
                
                <Box h="350px">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                      data={topBusinessesByKeywords.map((b, i) => ({
                        name: b.name.split(' ').slice(0, 2).join(' '),
                        keywords: b.keywordCount,
                        fill: COLORS.chart[i % COLORS.chart.length]
                      }))}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={borderColor} />
                      <XAxis type="number" tick={{ fontSize: 12 }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={75} />
                      <RechartsTooltip 
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            return (
                              <Box bg={glassBg} p={3} borderRadius="lg" boxShadow="lg">
                                <Text fontWeight="bold">{payload[0].payload.name}</Text>
                                <Text>{formatNum(payload[0].value)} keywords</Text>
                              </Box>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar dataKey="keywords" radius={[0, 4, 4, 0]}>
                        {topBusinessesByKeywords.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS.chart[index % COLORS.chart.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Box>

              {/* Keyword Difficulty Distribution */}
              <Box bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} p={6}>
                <HStack justify="space-between" mb={4}>
                  <VStack align="start" spacing={0}>
                    <Heading size="md">Keyword Difficulty</Heading>
                    <Text fontSize="sm" color={subtitleColor}>Distribution across market</Text>
                  </VStack>
                </HStack>
                
                <Box h="350px">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={keywordDifficultyDistribution}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={3}
                        dataKey="value"
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        labelLine={{ stroke: '#4A5568', strokeWidth: 1 }}
                      >
                        {keywordDifficultyDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip 
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            return (
                              <Box bg={glassBg} p={3} borderRadius="lg" boxShadow="lg">
                                <Text fontWeight="bold">{payload[0].name}</Text>
                                <Text>{formatNum(payload[0].value)} keywords</Text>
                              </Box>
                            );
                          }
                          return null;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            </Grid>

            {/* Business Keyword Table */}
            <Box bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} mt={6} overflow="hidden">
              <Box p={4} borderBottomWidth="1px" borderColor={borderColor}>
                <Heading size="md">All Businesses - Keyword Metrics</Heading>
              </Box>
              <TableContainer>
                <Table variant="simple" size="sm">
                  <Thead bg={statBg}>
                    <Tr>
                      <Th>Business</Th>
                      <Th isNumeric>Keywords</Th>
                      <Th isNumeric>Organic Clicks</Th>
                      <Th isNumeric>Organic Value</Th>
                      <Th isNumeric>Domain Strength</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {topBusinessesByKeywords.map((business, index) => (
                      <Tr 
                        key={index}
                        cursor="pointer"
                        _hover={{ bg: hoverBg }}
                        onClick={() => handleBusinessClick(business)}
                      >
                        <Td>
                          <HStack spacing={3}>
                            <Avatar size="sm" name={business.name} src={businessImages[business.maps_uri]} />
                            <VStack align="start" spacing={0}>
                              <Text fontWeight="medium" fontSize="sm">{business.name}</Text>
                              <Text fontSize="xs" color={subtitleColor}>{business.spyfu_data?.domain}</Text>
                            </VStack>
                          </HStack>
                        </Td>
                        <Td isNumeric>
                          <Badge colorScheme="purple">{formatNum(business.keywordCount)}</Badge>
                        </Td>
                        <Td isNumeric>
                          <Text fontWeight="medium">{formatNum(business.organicClicks)}</Text>
                        </Td>
                        <Td isNumeric>
                          <Text fontWeight="medium" color="green.500">{formatCurrency(business.organicValue)}</Text>
                        </Td>
                        <Td isNumeric>
                          <Progress 
                            value={business.domainStrength} 
                            max={100} 
                            colorScheme={business.domainStrength >= 50 ? 'green' : business.domainStrength >= 30 ? 'yellow' : 'red'}
                            size="sm"
                            w="80px"
                            borderRadius="full"
                          />
                          <Text fontSize="xs" color={subtitleColor}>{business.domainStrength}/100</Text>
                        </Td>
                        <Td>
                          <Button 
                            size="xs" 
                            colorScheme="purple" 
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              loadDomainKeywords(business.spyfu_data?.domain);
                              setActiveTab(4);
                            }}
                          >
                            Analyze
                          </Button>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>
          </TabPanel>

          {/* Top Keywords Panel */}
          <TabPanel p={0}>
            <Box bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} p={6}>
              <HStack justify="space-between" mb={6}>
                <VStack align="start" spacing={1}>
                  <HStack>
                    <Icon as={FaChartLine} color="purple.500" />
                    <Heading size="md">Top Keywords by Search Volume</Heading>
                  </HStack>
                  <Text fontSize="sm" color={subtitleColor}>Highest search volume keywords across all businesses</Text>
                </VStack>
                <Badge colorScheme="purple" fontSize="sm" px={3} py={1}>
                  {keywordStats?.topKeywords?.length || 0} keywords
                </Badge>
              </HStack>
              
              <VStack spacing={3} align="stretch">
                {keywordStats?.topKeywords?.map((kw, index) => (
                  <HStack
                    key={index}
                    p={4}
                    bg={statBg}
                    borderRadius="lg"
                    _hover={{ bg: hoverBg }}
                    transition="all 0.2s"
                  >
                    <Badge colorScheme="purple" variant="solid" px={3} py={1} minW="50px" textAlign="center">
                      #{kw.rank || '-'}
                    </Badge>
                    <VStack align="start" spacing={0} flex={1}>
                      <Text fontWeight="medium" fontSize="md">{kw.keyword}</Text>
                      <Text fontSize="xs" color={subtitleColor}>{kw.businessName}</Text>
                    </VStack>
                    <HStack spacing={4}>
                      <VStack spacing={0} align="end">
                        <HStack>
                          <Icon as={FaEye} color="green.500" boxSize={3} />
                          <Text fontWeight="bold" color="green.600">{formatNum(kw.searchVolume || 0)}</Text>
                        </HStack>
                        <Text fontSize="xs" color={subtitleColor}>volume/mo</Text>
                      </VStack>
                      <VStack spacing={0} align="end">
                        <Badge colorScheme={getKeywordDifficultyColor(kw.keywordDifficulty || 0)} variant="subtle">
                          {kw.keywordDifficulty || 0}
                        </Badge>
                        <Text fontSize="xs" color={subtitleColor}>difficulty</Text>
                      </VStack>
                      <VStack spacing={0} align="end">
                        <Text fontWeight="bold" color="blue.500">{formatCurrency(kw.cpc || 0)}</Text>
                        <Text fontSize="xs" color={subtitleColor}>CPC</Text>
                      </VStack>
                    </HStack>
                  </HStack>
                ))}
              </VStack>
            </Box>
          </TabPanel>

          {/* Opportunities Panel */}
          <TabPanel p={0}>
            <Box bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} p={6}>
              <HStack justify="space-between" mb={6}>
                <VStack align="start" spacing={1}>
                  <HStack>
                    <Icon as={FaLightbulb} color="yellow.500" />
                    <Heading size="md">Keyword Opportunities</Heading>
                  </HStack>
                  <Text fontSize="sm" color={subtitleColor}>
                    High volume keywords with low difficulty - ideal targets for SEO
                  </Text>
                </VStack>
                <Badge colorScheme="green" fontSize="sm" px={3} py={1}>
                  {keywordStats?.opportunities?.length || 0} opportunities
                </Badge>
              </HStack>
              
              {keywordStats?.opportunities?.length > 0 ? (
                <VStack spacing={3} align="stretch">
                  {keywordStats.opportunities.map((kw, index) => (
                    <HStack
                      key={index}
                      p={4}
                      bg="green.50"
                      borderWidth="1px"
                      borderColor="green.200"
                      borderRadius="lg"
                      _hover={{ bg: 'green.100' }}
                      transition="all 0.2s"
                    >
                      <Badge colorScheme="orange" variant="solid" px={3} py={1} minW="50px" textAlign="center">
                        #{kw.rank || '-'}
                      </Badge>
                      <VStack align="start" spacing={0} flex={1}>
                        <Text fontWeight="medium" fontSize="md">{kw.keyword}</Text>
                        <Text fontSize="xs" color={subtitleColor}>{kw.businessName}</Text>
                      </VStack>
                      <HStack spacing={4}>
                        <Tag size="md" colorScheme="green" variant="solid">
                          <TagLeftIcon as={FaEye} />
                          <TagLabel>{formatNum(kw.searchVolume || 0)}</TagLabel>
                        </Tag>
                        <Tag size="md" colorScheme="green" variant="outline">
                          <TagLabel>Diff: {kw.keywordDifficulty || 0}</TagLabel>
                        </Tag>
                        <Tooltip label="High potential - this keyword has high volume but low competition" hasArrow>
                          <Tag size="md" colorScheme="yellow" variant="solid">
                            <TagLeftIcon as={FaRocket} />
                            <TagLabel>Opportunity</TagLabel>
                          </Tag>
                        </Tooltip>
                      </HStack>
                    </HStack>
                  ))}
                </VStack>
              ) : (
                <Flex direction="column" align="center" py={12}>
                  <Icon as={FaSearch} color="gray.300" boxSize={12} mb={4} />
                  <Text color={subtitleColor} fontSize="lg">No keyword opportunities found</Text>
                  <Text color={mutedColor} fontSize="sm">Try adjusting the difficulty or volume thresholds</Text>
                </Flex>
              )}
            </Box>
          </TabPanel>

          {/* Keyword Research Panel */}
          <TabPanel p={0}>
            <Box bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} p={6}>
              <Heading size="md" mb={4}>
                <Icon as={FaSearch} mr={2} color="purple.500" />
                Keyword Research Tool
              </Heading>
              <Text color={subtitleColor} mb={6}>
                Enter a keyword to discover related keywords, search volumes, difficulty scores, and CPC data.
              </Text>
              
              <HStack mb={6}>
                <InputGroup size="lg">
                  <InputLeftElement>
                    <Icon as={FaSearch} color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Enter a keyword (e.g., plumber, dentist, restaurant)..."
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleKeywordSearch()}
                  />
                </InputGroup>
                <Button
                  colorScheme="purple"
                  size="lg"
                  px={8}
                  onClick={handleKeywordSearch}
                  isLoading={isSearching}
                  loadingText="Searching..."
                >
                  Research
                </Button>
              </HStack>

              {/* Search Results */}
              {keywordSearchResults?.data?.results?.length > 0 && (
                <Box>
                  <HStack justify="space-between" mb={4}>
                    <Heading size="sm">
                      Results for "{searchKeyword}"
                    </Heading>
                    <Badge colorScheme="purple">{keywordSearchResults.data.results.length} keywords found</Badge>
                  </HStack>
                  
                  <TableContainer>
                    <Table variant="simple" size="sm">
                      <Thead bg={statBg}>
                        <Tr>
                          <Th>Keyword</Th>
                          <Th isNumeric>Search Volume</Th>
                          <Th isNumeric>Difficulty</Th>
                          <Th isNumeric>CPC</Th>
                          <Th isNumeric>Competition</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {keywordSearchResults.data.results.slice(0, 50).map((kw, index) => (
                          <Tr key={index} _hover={{ bg: hoverBg }}>
                            <Td>
                              <Text fontWeight="medium">{kw.keyword || kw.term}</Text>
                            </Td>
                            <Td isNumeric>
                              <Badge colorScheme="green">{formatNum(kw.searchVolume || kw.exactLocalMonthlySearchVolume || 0)}</Badge>
                            </Td>
                            <Td isNumeric>
                              <Badge colorScheme={getKeywordDifficultyColor(kw.keywordDifficulty || kw.seoClicksPercent || 0)}>
                                {kw.keywordDifficulty || kw.seoClicksPercent || 0}
                              </Badge>
                            </Td>
                            <Td isNumeric>
                              <Text>{formatCurrency(kw.cpc || kw.costPerClick || 0)}</Text>
                            </Td>
                            <Td isNumeric>
                              <Text>{kw.paidCompetitors || kw.numberOfAdvertisers || 0}</Text>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {keywordSearchResults && !keywordSearchResults?.data?.results?.length && (
                <Alert status="info" borderRadius="lg">
                  <AlertIcon />
                  <AlertDescription>No results found for "{searchKeyword}". Try a different keyword.</AlertDescription>
                </Alert>
              )}
            </Box>
          </TabPanel>

          {/* Domain Analysis Panel */}
          <TabPanel p={0}>
            <Box bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} p={6}>
              <Heading size="md" mb={4}>
                <Icon as={FaGlobe} mr={2} color="blue.500" />
                Domain Keyword Analysis
              </Heading>
              <Text color={subtitleColor} mb={6}>
                Select a business to analyze their organic and paid keyword strategy.
              </Text>
              
              <HStack mb={6} flexWrap="wrap" gap={2}>
                {businessesWithKeywords.slice(0, 10).map((business, index) => (
                  <Button
                    key={index}
                    size="sm"
                    variant={selectedDomain === business.spyfu_data?.domain ? 'solid' : 'outline'}
                    colorScheme="blue"
                    onClick={() => loadDomainKeywords(business.spyfu_data?.domain)}
                    isLoading={isDomainLoading && selectedDomain === business.spyfu_data?.domain}
                  >
                    {business.name.split(' ').slice(0, 2).join(' ')}
                  </Button>
                ))}
              </HStack>

              {isDomainLoading && (
                <Flex justify="center" py={12}>
                  <Spinner size="xl" color="blue.500" />
                </Flex>
              )}

              {selectedDomain && !isDomainLoading && (
                <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6}>
                  {/* Organic Keywords */}
                  <Box>
                    <HStack mb={4}>
                      <Icon as={FaChartLine} color="green.500" />
                      <Heading size="sm">Organic Keywords</Heading>
                      <Badge colorScheme="green">{domainKeywords.organic?.length || 0}</Badge>
                    </HStack>
                    
                    {domainKeywords.organic?.length > 0 ? (
                      <VStack spacing={2} align="stretch" maxH="400px" overflowY="auto">
                        {domainKeywords.organic.slice(0, 20).map((kw, index) => (
                          <HStack key={index} p={3} bg={statBg} borderRadius="md" fontSize="sm">
                            <Badge colorScheme="green" minW="35px" textAlign="center">#{kw.rank || kw.rankingPosition || '-'}</Badge>
                            <Text flex={1} fontWeight="medium" noOfLines={1}>{kw.keyword || kw.term}</Text>
                            <Tag size="sm" colorScheme="blue">{formatNum(kw.searchVolume || kw.exactLocalMonthlySearchVolume || 0)}</Tag>
                          </HStack>
                        ))}
                      </VStack>
                    ) : (
                      <Text color={subtitleColor}>No organic keywords found</Text>
                    )}
                  </Box>

                  {/* Paid Keywords */}
                  <Box>
                    <HStack mb={4}>
                      <Icon as={FaDollarSign} color="orange.500" />
                      <Heading size="sm">Paid Keywords</Heading>
                      <Badge colorScheme="orange">{domainKeywords.paid?.length || 0}</Badge>
                    </HStack>
                    
                    {domainKeywords.paid?.length > 0 ? (
                      <VStack spacing={2} align="stretch" maxH="400px" overflowY="auto">
                        {domainKeywords.paid.slice(0, 20).map((kw, index) => (
                          <HStack key={index} p={3} bg={statBg} borderRadius="md" fontSize="sm">
                            <Badge colorScheme="orange" minW="35px" textAlign="center">#{kw.rank || kw.rankingPosition || '-'}</Badge>
                            <Text flex={1} fontWeight="medium" noOfLines={1}>{kw.keyword || kw.term}</Text>
                            <Tag size="sm" colorScheme="green">{formatCurrency(kw.cpc || kw.costPerClick || 0)}</Tag>
                          </HStack>
                        ))}
                      </VStack>
                    ) : (
                      <Text color={subtitleColor}>No paid keywords found</Text>
                    )}
                  </Box>
                </Grid>
              )}

              {!selectedDomain && !isDomainLoading && (
                <Flex direction="column" align="center" py={12}>
                  <Icon as={FaGlobe} color="gray.300" boxSize={12} mb={4} />
                  <Text color={subtitleColor}>Select a business above to analyze their keywords</Text>
                </Flex>
              )}
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Business Review Modal */}
      <BusinessReviewModal
        isOpen={isBusinessModalOpen}
        onClose={closeBusinessModal}
        business={selectedBusiness}
        yearData={yearData}
        sortedByTotal={sortedByTotal}
        sortedBySentiment={sortedBySentiment}
        sortedByGrowth={sortedByGrowth}
        sortedByTraffic={sortedByTraffic}
        demoMode={demoMode}
        demoTrafficHistory={demoMode ? demoData?.modalTrafficHistory : undefined}
        demoPricingData={demoMode ? demoData?.buildDemoPricing : undefined}
      />
    </Box>
  );
};

export default SpyfuKeywordDashboard;
